/**
 * The single tracking layer for the platform.
 *
 * Everything the business needs to measure goes through `track()`. No component
 * anywhere should embed a vendor script or call `gtag`/`fbq`/`ttq` directly —
 * that is how sites end up with the same conversion counted three times and no
 * way to turn any of it off.
 *
 * How it works:
 *  1. Vendors are configured entirely by environment variable. An unset id means
 *     that vendor simply does not exist; nothing is loaded and nothing is sent.
 *  2. Scripts load lazily, and only once the relevant consent category is granted
 *     (see `consent.ts`). Revoking consent stops further sends.
 *  3. Canonical event names are declared once, below, and translated per vendor
 *     at the edge. Product code speaks one vocabulary.
 *  4. Events fired before the scripts finish loading are queued, not dropped.
 *
 * Environment variables (all optional):
 *   VITE_GA_MEASUREMENT_ID     G-XXXXXXX        Google Analytics 4
 *   VITE_GOOGLE_ADS_ID         AW-XXXXXXXXX     Google Ads conversion linker
 *   VITE_GTM_ID                GTM-XXXXXXX      Google Tag Manager (use instead of GA4 if preferred)
 *   VITE_META_PIXEL_ID         numeric          Meta / Facebook / Instagram
 *   VITE_TIKTOK_PIXEL_ID       string
 *   VITE_LINKEDIN_PARTNER_ID   numeric
 */

import { getAttributionForEvent } from './attribution';
import { isAllowed, onConsentChange } from './consent';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: any;
    ttq?: any;
    lintrk?: (...args: any[]) => void;
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
  }
}

/**
 * Canonical events. These mirror actions the platform genuinely has — there is no
 * `add_to_wishlist` here because the product has no wishlist.
 */
export type AnalyticsEvent =
  | 'page_view'
  | 'view_course'
  | 'view_course_list'
  | 'search'
  | 'add_to_cart'
  | 'start_registration'
  | 'complete_registration'
  | 'login'
  | 'start_course'
  | 'complete_lesson'
  | 'start_quiz'
  | 'complete_quiz'
  | 'view_result'
  | 'view_pricing'
  | 'begin_checkout'
  | 'purchase'
  | 'apply_scholarship'
  /* Fired per step of the multi-step scholarship form. Declared here
     because `track()` only accepts names in this union — omitting it
     failed `tsc -b` and took the production build down with it. */
  | 'scholarship_application_step'
  | 'register_event'
  | 'newsletter_signup'
  | 'contact_submitted'
  | 'partnership_enquiry'
  /**
   * The partner (affiliate) programme, at /partners.
   *
   * Two events, not one, because the page has two distinct moments worth
   * measuring and only the second is a conversion. `partner_apply_start` fires
   * on the first real interaction with the form; `partner_apply_submitted`
   * fires on an application the server accepted. Without the second, a
   * partner-recruitment campaign has nothing to optimise toward and Meta
   * silently falls back to counting registrations — which every other campaign
   * also produces, so the channel becomes unreadable rather than merely
   * unoptimised.
   */
  | 'partner_apply_start'
  | 'partner_apply_submitted'
  | 'select_promotion'
  /* The free-resource funnel: /free/<slug>. `lead_magnet_success` is the
     conversion — it is what maps to Meta's `Lead` — while `submit` fires
     before the request and so also counts the attempts that failed
     validation or the network. Never carry the person's name, email or
     phone in these params; the resource slug and campaign are the labels
     the business put on its own links, and are all a vendor needs. */
  | 'lead_magnet_view'
  | 'lead_magnet_form_start'
  | 'lead_magnet_submit'
  | 'lead_magnet_success'
  | 'lead_magnet_download'
  | 'lead_magnet_course_click'
  /* A `/lp/<slug>` campaign landing page. Distinct from `lead_magnet_view`
     because the two pages ask for different things — one for an address, one
     for a click through to a course — but both are landing pages that paid
     traffic arrives on, so both map to the same first-party funnel step. */
  | 'campaign_page_view'
  /* Somebody tapping through to the WhatsApp group or the Telegram channel.
     Carries which group and where the button was, never who tapped it. */
  | 'community_join_click'
  /* Somebody choosing to open a WhatsApp chat with us rather than wait on the
     email. Distinct from `community_join_click`: that one joins a group of
     strangers, this one starts a one-to-one conversation, and they convert at
     very different rates. Carries the resource and the placement, never the
     number. */
  | 'whatsapp_chat_click'
  /* Somebody following the Telegram invite. Separate from
     `community_join_click`, which covers the WhatsApp group as well: only the
     Telegram link can carry a payload that ties the join back to a lead, so
     only this one has a measurable other end. Carries the placement and the
     course, never who tapped it. */
  | 'telegram_join_click'
  /* The contact capture on a course sales page — the surface that exists
     because a direct-to-course advertisement used to end at a page nobody
     bought from on a first visit, leaving no way to reach the person again.
     `course_contact_success` is the conversion and maps to Meta's `Lead`;
     `course_contact_submit` fires before the request, so it also counts the
     attempts that failed validation or the network. Never carry the name or
     the address in these params. */
  | 'course_contact_form_start'
  | 'course_contact_submit'
  | 'course_contact_success'
  /* The course conversion funnel: /course-details/<slug> through to the first
     lesson opened after paying. `view_course` already existed and stays the
     canonical "somebody looked at a course"; `course_sales_page_view` is
     narrower and fires only on the full sales page, which is what the
     conversion rates on the admin dashboard are computed against.

     Every one of these is also written first-party — see
     `lib/conversionTracking.ts`. The vendor copy is for bidding; the
     first-party copy is what the business can actually query, because a third
     of visitors never grant the consent these depend on. */
  | 'course_sales_page_view'
  | 'course_syllabus_expand'
  | 'course_preview_interaction'
  | 'course_enrol_click'
  | 'promo_code_apply'
  | 'payment_initiated'
  | 'payment_failed'
  | 'course_access_granted'
  | 'first_lesson_started';

export type EventParams = Record<string, string | number | boolean | undefined>;

const env = import.meta.env;
const ids = {
  ga: env.VITE_GA_MEASUREMENT_ID as string | undefined,
  googleAds: env.VITE_GOOGLE_ADS_ID as string | undefined,
  gtm: env.VITE_GTM_ID as string | undefined,
  meta: env.VITE_META_PIXEL_ID as string | undefined,
  tiktok: env.VITE_TIKTOK_PIXEL_ID as string | undefined,
  linkedin: env.VITE_LINKEDIN_PARTNER_ID as string | undefined,
};

const DEBUG = env.DEV;

/* ------------------------------------------------------------------ loading */

const loadedScripts = new Set<string>();

const injectScript = (key: string, src: string, attrs: Record<string, string> = {}): void => {
  if (loadedScripts.has(key) || typeof document === 'undefined') return;
  loadedScripts.add(key);
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  Object.entries(attrs).forEach(([name, value]) => script.setAttribute(name, value));
  document.head.appendChild(script);
};

const ensureDataLayer = (): void => {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // Must push `arguments` itself, not a copy — gtag.js reads the arguments object.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
};

const loadGoogle = (): void => {
  if (!ids.ga && !ids.googleAds && !ids.gtm) return;
  ensureDataLayer();

  if (ids.gtm) {
    injectScript('gtm', `https://www.googletagmanager.com/gtm.js?id=${ids.gtm}`);
    window.dataLayer!.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    return;
  }

  const primary = ids.ga ?? ids.googleAds!;
  injectScript('gtag', `https://www.googletagmanager.com/gtag/js?id=${primary}`);
  window.gtag!('js', new Date());
  // Page views are sent explicitly on route change; the SPA would otherwise only
  // ever report the entry URL.
  if (ids.ga) window.gtag!('config', ids.ga, { send_page_view: false });
  if (ids.googleAds) window.gtag!('config', ids.googleAds);
};

const loadMeta = (): void => {
  if (!ids.meta || loadedScripts.has('meta')) return;
  loadedScripts.add('meta');
  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    t = b.createElement(e) as HTMLScriptElement; t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode!.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', ids.meta);
};

const loadTikTok = (): void => {
  if (!ids.tiktok || loadedScripts.has('tiktok')) return;
  loadedScripts.add('tiktok');
  /* eslint-disable */
  (function (w: any, d: Document, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
    ttq.setAndDefer = function (obj: any, method: string) {
      obj[method] = function () { obj.push([method].concat(Array.prototype.slice.call(arguments, 0))); };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.load = function (id: string) {
      const url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {}; ttq._i[id] = []; ttq._i[id]._u = url;
      ttq._t = ttq._t || {}; ttq._t[id] = +new Date(); ttq._o = ttq._o || {}; ttq._o[id] = {};
      const script = d.createElement('script');
      script.async = true; script.src = `${url}?sdkid=${id}&lib=${t}`;
      const first = d.getElementsByTagName('script')[0];
      first.parentNode!.insertBefore(script, first);
    };
    ttq.load(ids.tiktok!);
  })(window, document, 'ttq');
  /* eslint-enable */
};

const loadLinkedIn = (): void => {
  if (!ids.linkedin || loadedScripts.has('linkedin')) return;
  window._linkedin_partner_id = ids.linkedin;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(ids.linkedin);
  injectScript('linkedin', 'https://snap.licdn.com/li/insight.min.js');
};

/* ------------------------------------------------------------------ vendor mapping */

/** Canonical name → GA4 recommended name, where one exists. */
const GA_NAMES: Partial<Record<AnalyticsEvent, string>> = {
  complete_registration: 'sign_up',
  start_registration: 'sign_up_start',
  view_course: 'view_item',
  view_course_list: 'view_item_list',
  view_pricing: 'view_item_list',
  select_promotion: 'select_promotion',
  lead_magnet_success: 'generate_lead',
  course_contact_success: 'generate_lead',
  course_sales_page_view: 'view_item',
  promo_code_apply: 'select_promotion',
  payment_initiated: 'add_payment_info',
};

/** Canonical name → Meta standard event. Anything absent is sent as custom. */
const META_NAMES: Partial<Record<AnalyticsEvent, string>> = {
  page_view: 'PageView',
  view_course: 'ViewContent',
  view_pricing: 'ViewContent',
  search: 'Search',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  complete_registration: 'CompleteRegistration',
  purchase: 'Purchase',
  newsletter_signup: 'Lead',
  contact_submitted: 'Lead',
  partnership_enquiry: 'Lead',
  partner_apply_submitted: 'Lead',
  apply_scholarship: 'Lead',
  lead_magnet_success: 'Lead',
  course_contact_success: 'Lead',
  course_sales_page_view: 'ViewContent',
  payment_initiated: 'AddPaymentInfo',
};

const TIKTOK_NAMES: Partial<Record<AnalyticsEvent, string>> = {
  view_course: 'ViewContent',
  search: 'Search',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  complete_registration: 'CompleteRegistration',
  purchase: 'CompletePayment',
  newsletter_signup: 'Subscribe',
  contact_submitted: 'Contact',
  partner_apply_submitted: 'SubmitForm',
  lead_magnet_success: 'SubmitForm',
  course_sales_page_view: 'ViewContent',
  payment_initiated: 'AddPaymentInfo',
};

/* ------------------------------------------------------------------ dispatch */

let initialised = false;
const queue: { event: AnalyticsEvent; params: EventParams }[] = [];

/**
 * Suppresses an identical event repeated within a short window. Guards against
 * the two things that actually cause double counting in a React SPA: an effect
 * that re-runs under StrictMode, and a button whose handler fires on both
 * `onClick` and a form `onSubmit`.
 */
const recentlySent = new Map<string, number>();
const DEDUPE_WINDOW_MS = 1200;

const isDuplicate = (event: AnalyticsEvent, params: EventParams): boolean => {
  const key = `${event}:${JSON.stringify(params)}`;
  const now = Date.now();
  const last = recentlySent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  recentlySent.set(key, now);
  if (recentlySent.size > 100) {
    recentlySent.forEach((time, k) => {
      if (now - time > DEDUPE_WINDOW_MS) recentlySent.delete(k);
    });
  }
  return false;
};

const clean = (params: EventParams): Record<string, string | number | boolean> =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '')) as Record<
    string,
    string | number | boolean
  >;

/**
 * The deduplication key, pulled out of the params before they are sent.
 *
 * Meta and TikTok both merge a browser event with a server event when the two
 * carry the same id — which is the only thing standing between us and every
 * purchase being counted twice, now that the server reports settled payments
 * through the Conversions API as well.
 *
 * It is passed as a parameter rather than a separate argument so that every
 * existing `track()` call site is unchanged, and it is *removed* from the
 * payload rather than left in it: GA4 would otherwise record it as a custom
 * dimension with one distinct value per purchase, which is both useless and a
 * cardinality problem.
 */
const DEDUPE_KEY = 'event_id';

const send = (event: AnalyticsEvent, params: EventParams): void => {
  const eventId = typeof params[DEDUPE_KEY] === 'string' ? (params[DEDUPE_KEY] as string) : undefined;
  const { [DEDUPE_KEY]: _omitted, ...rest } = params;
  const payload = clean({ ...rest, ...getAttributionForEvent() });

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.debug('[analytics]', event, payload);
  }

  if (isAllowed('analytics')) {
    if (ids.gtm && window.dataLayer) {
      window.dataLayer.push({ event, ...payload });
    } else if (window.gtag && ids.ga) {
      window.gtag('event', GA_NAMES[event] ?? event, payload);
    }
  }

  if (isAllowed('advertising')) {
    if (window.gtag && ids.googleAds && (event === 'purchase' || event === 'complete_registration')) {
      window.gtag('event', 'conversion', { send_to: ids.googleAds, ...payload });
    }
    if (window.fbq) {
      const standard = META_NAMES[event];
      // The fourth argument is Meta's deduplication envelope. Omitted entirely
      // when there is no id, rather than passed as `{ eventID: undefined }` —
      // the pixel treats an empty envelope as a distinct event.
      if (eventId) window.fbq(standard ? 'track' : 'trackCustom', standard ?? event, payload, { eventID: eventId });
      else window.fbq(standard ? 'track' : 'trackCustom', standard ?? event, payload);
    }
    if (window.ttq) {
      if (eventId) window.ttq.track(TIKTOK_NAMES[event] ?? event, payload, { event_id: eventId });
      else window.ttq.track(TIKTOK_NAMES[event] ?? event, payload);
    }
    if (window.lintrk && (event === 'purchase' || event === 'complete_registration')) {
      window.lintrk('track', { conversion_id: event });
    }
  }
};

/**
 * An unset id means the vendor is skipped silently, which is the right runtime
 * behaviour and a terrible failure mode for a launch: a build with every id
 * blank looks identical to a healthy one, and the first sign of trouble is a
 * campaign that has spent its budget against no conversion data at all.
 *
 * Warned once, loudly, rather than gated — a missing pixel must never stop the
 * site from serving.
 */
let configurationWarned = false;

const warnIfUnconfigured = (): void => {
  if (configurationWarned) return;
  configurationWarned = true;

  const configured = Object.entries(ids)
    .filter(([, value]) => Boolean(value))
    .map(([name]) => name);

  if (configured.length === 0) {
    // eslint-disable-next-line no-console
    console.warn(
      '[analytics] No vendor is configured — no GA4, Ads, Meta, TikTok or LinkedIn tag will load, ' +
        'and no conversion will be recorded anywhere. Set VITE_GA_MEASUREMENT_ID / VITE_META_PIXEL_ID / ' +
        'VITE_TIKTOK_PIXEL_ID / VITE_GOOGLE_ADS_ID in the build environment before spending on advertising.'
    );
    return;
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.debug(`[analytics] configured vendors: ${configured.join(', ')}`);
  }
};

/** Loads whichever vendors the current consent state permits. Safe to call repeatedly. */
export const initAnalytics = (): void => {
  if (typeof window === 'undefined') return;

  warnIfUnconfigured();

  if (isAllowed('analytics')) loadGoogle();
  if (isAllowed('advertising')) {
    loadGoogle(); // Google Ads shares the gtag runtime with GA4.
    loadMeta();
    loadTikTok();
    loadLinkedIn();
  }

  if (!initialised) {
    initialised = true;
    onConsentChange(() => {
      initAnalytics();
      flush();
    });
  }
  flush();
};

const flush = (): void => {
  if (queue.length === 0) return;
  const pending = queue.splice(0, queue.length);
  pending.forEach(({ event, params }) => send(event, params));
};

/**
 * The deterministic id for a purchase, identical to the one the server sends.
 *
 * Derived from the Paystack reference rather than randomly generated, because
 * the two copies of this event are produced by two processes that never talk to
 * each other — the browser knows the reference, and that is the whole basis of
 * the agreement. It must stay in step with `purchaseEventId` in
 * `backend/src/services/adPlatforms/index.ts`.
 */
export const purchaseEventId = (reference: string): string => `mdx-purchase-${reference}`;

/**
 * Record something the visitor did. The only tracking call product code should make.
 *
 * @example track('view_course', { course_slug: slug, course_title: title, value: price, currency: 'NGN' })
 */
export const track = (event: AnalyticsEvent, params: EventParams = {}): void => {
  if (typeof window === 'undefined') return;
  if (isDuplicate(event, params)) return;

  // Before a decision is made nothing is transmitted, but the events are held so
  // that a visitor who accepts on the second screen is not missing the first.
  if (!isAllowed('analytics') && !isAllowed('advertising')) {
    if (queue.length < 30) queue.push({ event, params });
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.debug('[analytics:queued]', event, params);
    }
    return;
  }

  send(event, params);
};

/**
 * Routes whose URL carries a secret, and the shape of the secret in it.
 *
 * These are single-factor credentials: the verification token is the entire
 * proof that somebody owns an address, the reset token is the entire proof they
 * may set a new password, and the download token grants a lead magnet for six
 * months. Sending any of them to Google, Meta or TikTok hands a third party —
 * and anyone with access to that vendor account — a working credential.
 *
 * The in-app path already avoids this for downloads by passing the token in
 * router state rather than the URL; the emailed link cannot, so it is redacted
 * here instead.
 */
const SECRET_IN_URL: Array<[RegExp, string]> = [
  [/^\/verify-email\/[^/?#]+/, '/verify-email/<redacted>'],
  [/^\/password\/reset\/[^/?#]+/, '/password/reset/<redacted>'],
];

/** Query parameters that are credentials rather than campaign labels. */
const SECRET_PARAMS = ['token', 'reference', 'trxref'];

/**
 * Strips credentials out of a URL before it is described to a vendor.
 *
 * Deliberately a redaction rather than a drop: knowing that somebody reached
 * the verification page is a legitimate and useful measurement. Knowing which
 * token they used is not measurement, it is a leak.
 */
export const redactUrl = (raw: string): string => {
  try {
    const url = new URL(raw, window.location.origin);
    for (const [pattern, replacement] of SECRET_IN_URL) {
      if (pattern.test(url.pathname)) url.pathname = url.pathname.replace(pattern, replacement);
    }
    for (const param of SECRET_PARAMS) {
      if (url.searchParams.has(param)) url.searchParams.set(param, '<redacted>');
    }
    return url.toString();
  } catch {
    return raw;
  }
};

const redactPath = (path: string): string => {
  let out = path;
  for (const [pattern, replacement] of SECRET_IN_URL) out = out.replace(pattern, replacement);
  return out;
};

/** Route-change page view. Called once from the router, never from a page. */
export const trackPageView = (path: string, title?: string): void => {
  track('page_view', {
    page_path: redactPath(path),
    page_title: title ?? document.title,
    page_location: redactUrl(window.location.href),
  });
  if (isAllowed('advertising') && window.ttq) window.ttq.page();
};

/**
 * Associates subsequent events with a signed-in user. Pass the internal id only —
 * never an email address or anything else that identifies the person to a vendor.
 */
export const identify = (userId: string): void => {
  if (!isAllowed('analytics')) return;
  if (window.gtag && ids.ga) window.gtag('config', ids.ga, { user_id: userId });
  if (window.dataLayer && ids.gtm) window.dataLayer.push({ event: 'identify', user_id: userId });
};
